import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react';
import { useNoticeToast } from '../lib/toast';
import { useFitHeight } from '../lib/fitHeight';
import { TopScrollbar } from '../components/TopScrollbar';
import {
  api, type AcctCorp, type BalanceCheck, type BankAccountRow, type BankBalanceResult, type BankCell, type BankUploadResult, type BankUploadRow,
} from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, money, useList, type Column, type ListResultLike, type Row } from '../components/DataTable';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';
import { CashBookTab, CashRulesTab } from './CashBook';
import { LiquidityTab } from './Liquidity';
import { InvoiceTab } from './Invoice';
import { DriverOrdersTab } from './DriverOrders';
import { DriverCustomersTab } from './DriverCustomers';
import { FineTab } from './Fine';
import ToplineTab from './Topline';
import { InvoiceRequestTab, useInvReqPulse } from './InvoiceRequest';
import { OpsTransitTab } from './OpsTransit';
import { InsuranceTab } from './Insurance';
import { CardCheckTab } from './CardCheck';
import { CashOutMatchTab } from './CashOutMatch';
import { AffilSettleTab } from './AffilSettle';
import { GabulTab } from './Gabul';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ACCT_PATH, acctKey, usePageTabs } from '../lib/pageTabs';
import { AutoMatchForm, DepositMatchForm } from './DepositMatch';
import { BankFinForm } from './BankFin';
import { BankInPlanForm, BankInPlanStrip } from './BankInPlan';
import type { DepKind } from '../lib/api';
import { useSticky, useAcctCorp } from '../lib/sticky';
import { useSort } from '../lib/sort';
import { useMenuOrder } from '../lib/menuOrder';
import MenuOrderEditor from '../components/MenuOrderEditor';
import { armCopy } from '../lib/cellCopy';

/**
 * 회계 — 1단계: 은행 입금 내역(#12) · 일자별 잔액(#13) · 은행 파일 업로드 · 계좌 관리 · 출납일보·반복 출금(#19).
 * 기획 정본 `plan/accounting.md` 5-A·5-C. 출납일보는 `CashBook.tsx`.
 *
 * 🔴 권한은 `accounting`(+관리자)만 — 서버가 막는다. 메뉴를 숨기는 건 편의일 뿐이다.
 * 🔴 법인은 **계좌번호로만** 정해진다(은행 파일의 별칭은 옛 회사명이다). 그래서 계좌리스트를 먼저 올린다.
 */

type Tab = 'invreq' | 'invoice' | 'affil' | 'dorder' | 'dcust' | 'fine' | 'topline' | 'tx' | 'balance' | 'cash' | 'rules' | 'liquidity' | 'upload' | 'accounts' | 'opsTransit' | 'insurance' | 'cards' | 'gabul' | 'outmatch';
const isTab = (x: unknown): x is Tab => TABS.some(([id]) => id === x);
/** 화면 탭 이름·키에 쓴다(App.tsx · #211). */
export const isAcctTab = isTab;
const TABS: [Tab, string][] = [
  ['invreq', '발행 요청'], ['invoice', '세금계산서'], ['affil', '관계법인 정산'],
  ['tx', '입금 내역'], ['balance', '일자별 잔액'], ['cash', '출납일보'], ['rules', '반복 출금'], ['outmatch', '출금 대조'], ['liquidity', '유동성 시재'],
  ['dorder', '기사 페이앱·카드·현금·다수'], ['dcust', '기사 거래처'], ['topline', '차인지급액 조정'], ['fine', '과태료'], ['gabul', '가불금·블랙리스트'],
  ['insurance', '보험'], ['cards', '카드 점검'], ['opsTransit', 'OPS 대중교통비·선지급'],
  ['upload', '은행 파일 올리기'], ['accounts', '계좌 관리'],
];
export const ACCT_TABS: readonly [string, string][] = TABS;

/**
 * 탭 묶음(2026-11-02 way 「하위메뉴가 너무 많아 무엇을 봐야 할지 혼란」) — 18개를 한 줄에 늘어놓지 않고
 * 묶음을 먼저 고르면 그 묶음의 탭만 보인다. 탭 id·주소(`?tab=`)는 그대로라 링크·화면 탭은 안 바뀐다.
 * 🔴 새 탭을 TABS 에 더하면 여기 묶음에도 넣는다(안 넣으면 화면에 탭 버튼이 안 나온다 — 아래 `GROUP_OF` 가 마지막 묶음으로 보낸다).
 */
type Group = 'bill' | 'bank' | 'driver' | 'cost' | 'setup';
const GROUPS: [Group, string, Tab[]][] = [
  ['bill', '세금계산서', ['invreq', 'invoice', 'affil']],
  ['bank', '통장·자금', ['tx', 'balance', 'cash', 'rules', 'outmatch', 'liquidity']],
  ['driver', '기사', ['dorder', 'dcust', 'topline', 'fine', 'gabul']],
  ['cost', '비용 점검', ['insurance', 'cards', 'opsTransit']],
  ['setup', '설정', ['upload', 'accounts']],
];
const GROUP_OF = (t: Tab): Group => GROUPS.find(([, , ts]) => ts.includes(t))?.[0] ?? 'setup';
type GroupTabs = Partial<Record<Group, Tab>>;
const isGroupTabs = (v: unknown): v is GroupTabs =>
  !!v && typeof v === 'object' && !Array.isArray(v) && Object.values(v).every((x) => isTab(x));

const today = () => {
  const d = new Date(Date.now() + 9 * 3600_000); // KST
  return d.toISOString().slice(0, 10);
};
// 조회 기본 기간 = 어제~오늘(2026-09-29 게시판 #42 — 예전엔 이번 달 1일부터).
const yesterday = () => new Date(Date.parse(`${today()}T00:00:00Z`) - 86400_000).toISOString().slice(0, 10);
/** 입금 내역 끌어 복사(#298) — 칸 글자 그대로 · 금액은 원화면 원 단위 숫자. */
const txt = (k: string) => (r: Row) => String(r[k] ?? '');
const amt = (k: string) => (r: Row) => (Number(r[k]) ? String(r.currency === 'KRW' ? Math.round(Number(r[k])) : Number(r[k])) : '');
const cur = (currency: string, v: unknown) => (currency === 'KRW' ? money(v) : `${currency} ${Number(v ?? 0).toLocaleString('ko-KR', { minimumFractionDigits: 2 })}`);

export default function Accounting() {
  const { me } = useMe();
  // `?req=번호`(팝업·챗 링크) · `?tab=invreq`(빨간 띠)로 들어오면 발행 요청 탭부터.
  const [params] = useSearchParams();
  const wantReq = !!params.get('req') || !!params.get('mcr') || params.get('tab') === 'invreq';
  // 마지막 탭은 계정에 기억한다(#37) — 링크로 들어오면(wantReq) 아래 effect 가 먼저 골라 기억보다 앞선다.
  const [lastTab, setLastTab] = useSticky<Tab>('acct.tab', wantReq ? 'invreq' : 'tx', { persist: true, valid: isTab });
  // 세부 탭마다 화면 탭이 따로다(#211) — 이 화면의 세부 탭은 **자기 주소의 `?tab=`** 로 정해진다(화면 탭이 붙들고 있는 주소).
  // 기억(acct.tab)은 「다음에 회계를 열 때 어느 탭부터」에만 쓴다.
  const urlTab = params.get('tab');
  const tab: Tab = isTab(urlTab) ? urlTab : wantReq ? 'invreq' : lastTab;
  useEffect(() => { if (tab !== lastTab) setLastTab(tab); }, [tab]);
  const navigate = useNavigate();
  const pageTabs = usePageTabs();
  // 세부 탭 누르기 = 그 세부 탭의 화면 탭으로 — 이미 열려 있으면 그 화면(입력·조건 그대로), 없으면 새로.
  const setTab = (id: Tab) => {
    const open = pageTabs.find((t) => t.key === acctKey(id));
    navigate(open ? `${open.loc.pathname}${open.loc.search}${open.loc.hash}` : `${ACCT_PATH}?tab=${id}`);
  };
  const invreq = useInvReqPulse();
  // 발행 요청 배지 = 새 요청·영업 답 + 처리 대기 취소·수정 요청(10-19 일원화).
  const invreqBadge = invreq?.acct ? invreq.acct.fresh.length + invreq.acct.replied.length + (invreq.acct.cancels?.length ?? 0) : 0;
  // 탭 순서도 계정마다(게시판 #160 — 상단 메뉴와 같은 방식). 순서는 묶음 안에서만 바꾼다.
  const [tabs, , setTabOrder] = useMenuOrder('acct.tab-order', TABS, ([id]) => id);
  const [orderOpen, setOrderOpen] = useState(false);
  const group = GROUP_OF(tab);
  const groupTabs = tabs.filter(([id]) => GROUP_OF(id) === group);
  // 묶음마다 마지막에 본 탭(계정 저장) — 묶음을 누르면 그 탭부터 연다.
  const [groupLast, setGroupLast] = useSticky<GroupTabs>('acct.group-tab', {}, { persist: true, valid: isGroupTabs });
  useEffect(() => { if (groupLast[group] !== tab) setGroupLast((p) => ({ ...p, [group]: tab })); }, [tab]);
  const openGroup = (g: Group) => {
    const want = groupLast[g];
    const first = tabs.find(([id]) => GROUP_OF(id) === g)?.[0];
    const to = want && GROUP_OF(want) === g ? want : first;
    if (to) setTab(to);
  };
  /** 이 묶음의 새 순서를 전체 순서 안 그 묶음 자리에 끼운다(다른 묶음 순서는 그대로). */
  const saveGroupOrder = (ids: string[] | null) => {
    const next = ids ?? TABS.filter(([id]) => GROUP_OF(id) === group).map(([id]) => id);
    let i = 0;
    setTabOrder(tabs.map(([id]) => (GROUP_OF(id) === group ? next[i++]! : id)));
  };
  const [corps, setCorps] = useState<AcctCorp[]>([]);
  const allowed = canScreen(me, 'accounting', 'read');

  useEffect(() => {
    if (!allowed) return;
    api.listAcctCorps().then((r) => setCorps(r.rows)).catch(() => setCorps([]));
  }, [allowed]);

  if (me && !allowed) {
    return (
      <div className="card p-5 text-sm text-ink-soft">
        회계 화면은 <b>회계</b> 권한이 있어야 볼 수 있습니다(은행 계좌·잔액이 들어 있습니다). 관리자에게 권한을 요청하세요.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* 제목·묶음·탭을 한 줄로 — 아래 표가 더 보이게(2026-09-26 사용자 요청). 설명은 제목 툴팁으로.
          묶음(11-02) = 왼쪽 상자 · 그 묶음의 탭만 오른쪽에. 발행 요청 배지는 묶음에도 띄운다(다른 묶음을 보고 있어도 보이게). */}
      <div className="flex flex-wrap items-center gap-0.5">
        <h1 className="mr-1.5 text-base font-bold"
            title="인사이드뱅크에서 받은 파일을 올리면 법인·계좌별로 자동으로 나뉘어 쌓입니다. 같은 파일을 두 번 올려도 한 번만 들어갑니다.">
          회계
        </h1>
        <div className="flex flex-wrap items-center gap-0.5 rounded-lg bg-canvas p-0.5" role="tablist" aria-label="회계 묶음">
          {GROUPS.map(([g, label]) => (
            <button key={g} type="button" role="tab" aria-selected={group === g} onClick={() => openGroup(g)}
                    className={`rounded-md px-2 py-0.5 text-[13px] ${group === g ? 'bg-white font-bold text-brand-700 shadow-sm' : 'font-medium text-ink-soft hover:text-ink'}`}>
              {label}
              {g === 'bill' && invreqBadge > 0 && <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{invreqBadge}</span>}
            </button>
          ))}
        </div>
        <span className="mx-1 text-ink-faint" aria-hidden>›</span>
        {groupTabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
                  className={`rounded-lg px-2 py-0.5 text-[13px] font-medium ${tab === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
            {label}
            {id === 'invreq' && invreqBadge > 0 && <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{invreqBadge}</span>}
          </button>
        ))}
        <button type="button" onClick={() => setOrderOpen(true)} title="탭 순서 바꾸기" aria-label="탭 순서 바꾸기"
                className="rounded-lg px-2 py-1 text-sm text-ink-faint hover:bg-canvas hover:text-ink-soft">
          ⇅
        </button>
      </div>
      {orderOpen && (
        <Modal width="max-w-md">
          <MenuOrderEditor title={`회계 › ${GROUPS.find(([g]) => g === group)?.[1] ?? ''} 탭 순서`} items={groupTabs.map(([id, label]) => ({ id, label }))}
                           onSave={saveGroupOrder} onReset={() => saveGroupOrder(null)} onClose={() => setOrderOpen(false)} />
        </Modal>
      )}
      {tab === 'invreq' && <InvoiceRequestTab corps={corps} />}
      {tab === 'tx' && <TxTab corps={corps} />}
      {tab === 'balance' && <BalanceTab corps={corps} />}
      {tab === 'invoice' && <InvoiceTab corps={corps} />}
      {tab === 'dorder' && <DriverOrdersTab corps={corps} />}
      {tab === 'dcust' && <DriverCustomersTab />}
      {tab === 'fine' && <FineTab corps={corps} />}
      {tab === 'topline' && <ToplineTab />}
      {tab === 'cash' && <CashBookTab corps={corps} />}
      {tab === 'rules' && <CashRulesTab corps={corps} />}
      {tab === 'insurance' && <InsuranceTab corps={corps} />}
      {tab === 'cards' && <CardCheckTab />}
      {tab === 'outmatch' && <CashOutMatchTab corps={corps} />}
      {tab === 'affil' && <AffilSettleTab />}
      {tab === 'opsTransit' && <OpsTransitTab corps={corps} />}
      {tab === 'liquidity' && <LiquidityTab corps={corps} />}
      {tab === 'gabul' && <GabulTab />}
      {tab === 'upload' && <UploadTab />}
      {tab === 'accounts' && <AccountsTab corps={corps} />}
    </div>
  );
}

function CorpSelect({ corps, value, onChange }: { corps: AcctCorp[]; value: number | null; onChange: (v: number | null) => void }) {
  return (
    <select className="field w-40" value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
      <option value="">전체 법인</option>
      {corps.map((c) => (
        <option key={c.corp_id} value={c.corp_id}>{c.parent_corp_id ? `└ ${c.short_name}` : c.short_name}</option>
      ))}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 입금 내역 (#12)
// ─────────────────────────────────────────────────────────────────────────────

/** 입금 구분 — 입금시트 「입금체크」 탭의 바탕색 그대로(회계팀이 이미 이 색으로 읽는다). */
const DEP_KIND: Record<DepKind, { label: string; bg: string; hint: string }> = {
  company: { label: '회사오더', bg: 'bg-sky-50', hint: '버스인 회사 오더' },
  driver: { label: '기사개인오더', bg: 'bg-yellow-50', hint: '기사 접수 앱으로 신청한 계산서' },
  finance: { label: '재무추가', bg: 'bg-green-50', hint: '정산부족분·사고보상비·보험료 환급·자동차세 환급·수리비·인센티브 등 재무추가 지급건' },
  vehicle: { label: '차량매각', bg: 'bg-teal-50', hint: '차량 매각 관련(기사인도금·매각 대금 등)' },
  purchase: { label: '차량구입', bg: 'bg-violet-50', hint: '차량 구입 관련(할부사 입금·취등록세 기사부담 등)' },
  check: { label: '체크대상', bg: 'bg-orange-100', hint: '정보 미확인 예금·전표 분개 체크건' },
  other: { label: '그 밖', bg: '', hint: '카드매출·페이앱·이자수익·관계법인·계좌간 이체' },
};
const KIND_KEYS = Object.keys(DEP_KIND) as DepKind[];
/** #166 — 입금전표 적요에 「확인필요」를 손으로 적은 줄은 구분과 상관없이 주황(옛 입금시트 「확인필요」 색). */
const CHECK_NEEDED_BG = 'bg-orange-200';

/**
 * 입금전표 적요 고치기. 비우면 연결된 계산서로 만든 자동 적요로 돌아간다.
 * 여러 줄을 붙여 넣으면(#150) 이 줄부터 아래 입금 줄에 한 줄씩 차례로 들어간다(시트에서 칸 여러 개를 복사해 붙이는 그대로).
 * 붙여 넣은 줄 중 빈 줄은 그 입금을 건너뛴다. 아래 줄이 모자라면 저장하지 않는다(잘려서 들어가는 것보다 낫다).
 */
function SlipNoteForm({ row, below, onClose, onDone }: { row: Record<string, unknown>; below: Record<string, unknown>[]; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState(row.slip_set ? String(row.slip ?? '') : '');
  const [lines, setLines] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (value: string | null) => {
    setBusy(true); setError(null);
    try { await api.annotateBankTx({ ids: [Number(row.bank_tx_id)], note: value }); onDone(); onClose(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const raw = e.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n').replace(/\n+$/, '');
    if (!raw.includes('\n')) return;
    e.preventDefault();
    setLines(raw.split('\n').map((l) => l.trim()));
  };
  const pairs = lines ? lines.map((l, i) => ({ r: below[i], note: l })) : [];
  const short = lines !== null && lines.length > below.length;
  const saveMany = async () => {
    const items = pairs.filter((p) => p.r && p.note).map((p) => ({ id: Number(p.r!.bank_tx_id), note: p.note.slice(0, 200) }));
    if (items.length === 0) return;
    setBusy(true); setError(null);
    try { await api.annotateBankTx({ notes: items }); onDone(); onClose(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  if (lines) {
    return (
      <div className="card space-y-3 p-5 text-sm">
        <h2 className="text-lg font-semibold">입금전표 적요 — 여러 줄 붙여넣기</h2>
        <p className="text-ink-soft">이 줄부터 아래로 {lines.length}줄이 차례로 들어갑니다. 빈 줄은 그 입금을 건너뜁니다.</p>
        <div className="max-h-96 overflow-auto rounded-lg border border-line">
          <table className="w-full text-xs">
            <thead className="bg-canvas text-ink-soft"><tr><th className="px-2 py-1 text-left">거래일자</th><th className="px-2 py-1 text-left">내용</th><th className="px-2 py-1 text-right">입금</th><th className="px-2 py-1 text-left">붙일 적요</th></tr></thead>
            <tbody>
              {pairs.map((p, i) => (
                <tr key={i} className="border-t border-line">
                  <td className="px-2 py-1">{p.r ? String(p.r.tx_date) : '—'}</td>
                  <td className="px-2 py-1">{p.r ? dash(p.r.memo) : <span className="text-red-600">아래 입금 줄 없음</span>}</td>
                  <td className="px-2 py-1 text-right">{p.r ? money(p.r.amount_in) : ''}</td>
                  <td className={`px-2 py-1 ${p.note ? '' : 'text-ink-faint'}`}>{p.note || '(건너뜀)'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {short && <ErrorBox message={`붙여 넣은 줄이 ${lines.length}줄인데 이 화면에서 아래 입금 줄은 ${below.length}줄뿐입니다. 한 쪽에 보이는 줄 수를 늘리거나 나눠서 붙여 넣어 주세요.`} />}
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost mr-auto" disabled={busy} onClick={() => setLines(null)}>한 줄만 적기</button>
          <button type="button" className="btn-ghost" onClick={onClose}>취소</button>
          <button type="button" className="btn-primary" disabled={busy || short || !pairs.some((p) => p.note)} onClick={saveMany}>
            {pairs.filter((p) => p.note).length}줄 저장
          </button>
        </div>
      </div>
    );
  }
  return (
    <form className="card space-y-3 p-5 text-sm" onSubmit={(e) => { e.preventDefault(); save(note.trim() || null); }}>
      <h2 className="text-lg font-semibold">입금전표 적요</h2>
      <p className="rounded-lg bg-canvas px-3 py-2">{String(row.tx_date)} · {String(row.corp_name)} · 내용 <b>{dash(row.memo)}</b> · 입금 <b>{money(row.amount_in)}</b></p>
      <input className="field w-full" autoFocus maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} onPaste={onPaste}
             placeholder={!row.slip_set && row.slip ? `자동: ${String(row.slip)}` : '예: 청구 경기78아6813 차량대금'} />
      <p className="text-xs text-ink-faint">비워 두면 연결된 계산서로 만듭니다 — 회사 「청구{'{번호}'} {'{요청자}'}」 · 기사 「■청구 {'{기사}'}」.</p>
      <p className="text-xs text-ink-faint">시트에서 여러 칸을 복사해 붙여 넣으면 이 줄부터 아래로 차례로 들어갑니다.</p>
      {error && <ErrorBox message={error} />}
      <div className="flex justify-end gap-2">
        {Boolean(row.slip_set) && <button type="button" className="btn-ghost mr-auto" disabled={busy} onClick={() => save(null)}>자동으로 되돌리기</button>}
        <button type="button" className="btn-ghost" onClick={onClose}>취소</button>
        <button type="submit" className="btn-primary" disabled={busy}>저장</button>
      </div>
    </form>
  );
}

/**
 * 계좌 고르기(#28) — 「파주 기사매출 [국민 791]」 처럼 법인·용도·은행·번호 끝 3자리. 법인을 고르면 그 법인 계좌만(지점 제외).
 * 원화·사용 계좌만 — 입금 내역에서 고를 일이 없는 외화·해지 계좌는 뺀다.
 */
function AccountSelect({ corps, corpId, value, onChange }: { corps: AcctCorp[]; corpId: number | null; value: number | null; onChange: (v: number | null) => void }) {
  const [accs, setAccs] = useState<BankAccountRow[]>([]);
  useEffect(() => { api.listBankAccounts().then((r) => setAccs(r.rows)).catch(() => setAccs([])); }, []);
  const inCorp = (a: BankAccountRow) => corpId === null || a.corp_id === corpId;
  const list = accs.filter((a) => a.is_active && a.currency === 'KRW' && inCorp(a));
  const label = (a: BankAccountRow) => {
    const tail = a.account_no.replace(/[^0-9]/g, '').slice(-3);
    const use = a.is_driver_acct ? '기사매출' : (a.alias ?? '');
    return `${a.corp_name}${use ? ` ${use}` : ''} [${a.bank} ${tail}]`;
  };
  return (
    <select className="field w-56" value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
      <option value="">전체 계좌</option>
      {list.map((a) => <option key={a.bank_account_id} value={a.bank_account_id} title={`${a.account_no} · ${a.product ?? ''}`}>{label(a)}</option>)}
    </select>
  );
}

function TxTab({ corps }: { corps: AcctCorp[] }) {
  const [from, setFrom] = useSticky('acct.tx.from', yesterday());
  const [to, setTo] = useSticky('acct.tx.to', today());
  const [corpId, setCorpId] = useAcctCorp();
  const [accountId, setAccountId] = useState<number | null>(null);
  const [direction, setDirection] = useSticky<'in' | 'out' | 'all'>('acct.tx.direction', 'in');
  const [term, setTerm] = useSticky('acct.tx.term', '');
  const [q, setQ] = useSticky('acct.tx.q', '');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('bank-tx', 200);
  const [match, setMatch] = useSticky<'all' | 'unmatched' | 'matched'>('acct.tx.match', 'all');
  const [kind, setKind] = useSticky<DepKind | 'none' | 'all'>('acct.tx.kind', 'all');
  const [noteRow, setNoteRow] = useState<Record<string, unknown> | null>(null);
  const [annotError, setAnnotError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [matching, setMatching] = useState<number | null>(null);
  const [finRow, setFinRow] = useState<number | null>(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false); // #351 수기 입금(추정)
  const [totals, setTotals] = useState<{ currency: string; n: number; amount_in: string; amount_out: string }[]>([]);
  // 머리글 정렬(#37) — 200줄씩 받으니 서버가 정렬한다(bank.ts TX_SORT). 정렬을 바꾸면 1쪽부터.
  const [sort, toggleSort] = useSort('acct.tx');
  // #172 적요 누르고 Ctrl+C 로 복사(#250 · 출납일보와 같은 알림) · 두 번 누르면 고치는 창.
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef(0);
  const say = useCallback((text: string) => {
    setToast(text);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2500);
  }, []);
  const copySlip = (value: string, el: HTMLElement) => {
    armCopy(value, el, (r) => say(r.ok
      ? `입금전표 적요 복사: ${value.length > 40 ? `${value.slice(0, 40)}…` : value}`
      : `복사하지 못했습니다: ${r.error}`));
  };

  const { rows, loading, error } = useList(async (alive) => {
    const r = await api.listBankTx({ dateFrom: from, dateTo: to, corpId, accountId, direction, q: q || undefined, limit: PAGE, offset: page * PAGE, match, kind, sort: sort?.key, dir: sort?.dir });
    if (alive()) setTotals(r.totals);
    // `total`(건수 숫자)은 넘기지 않는다 — useList 는 `total` 을 총계 **줄**(객체)로 읽는다.
    return { rows: r.rows } as ListResultLike;
  }, [from, to, corpId, accountId, direction, q, page, PAGE, match, kind, nonce, sort]);

  const setRowKind = async (id: number, k: DepKind | null) => {
    setAnnotError(null);
    try { await api.annotateBankTx({ ids: [id], kind: k }); setNonce((n) => n + 1); }
    catch (e) { setAnnotError(e instanceof Error ? e.message : String(e)); }
  };

  const columns: Column[] = [
    // phone: 폰에선 거래일자를 왼쪽에 고정하고 법인·계좌번호·거래점·전표 적요는 뺀다(모바일 7차).
    // #298 — 모든 칸에 `copy` 를 달아 어느 칸이든 끌어 네모로 골라 복사(엑셀처럼).
    { key: 'tx_date', label: '거래일자', phone: 'pin', copy: txt('tx_date') },
    // 입금시트 「입금체크」 색 구분. 손으로 안 정했으면 연결에서 읽은 값(자동)을 보여 준다.
    { key: 'kind', label: '구분', noSort: true, copy: (r) => (Number(r.amount_in) > 0 && r.kind ? DEP_KIND[r.kind as DepKind]?.label ?? '' : ''), render: (r) => {
      if (!(Number(r.amount_in) > 0)) return '';
      const k = (r.kind ?? null) as DepKind | null;
      return (
        <select className={`rounded border border-line bg-transparent px-1 py-0.5 text-xs ${r.kind_set ? 'font-medium' : 'text-ink-soft'}`}
                value={r.kind_set ? String(k) : ''} disabled={r.fin_mon != null}
                title={r.fin_mon != null ? '재무추가 지급으로 넣은 입금 — 구분을 바꾸려면 「재무추가」 칸에서 먼저 되돌리세요' : k ? DEP_KIND[k].hint : '미분류'}
                onChange={(e) => setRowKind(Number(r.bank_tx_id), (e.target.value || null) as DepKind | null)}>
          <option value="">{k && !r.kind_set ? `자동 · ${DEP_KIND[k].label}` : '미분류'}</option>
          {KIND_KEYS.map((x) => <option key={x} value={x}>{DEP_KIND[x].label}</option>)}
        </select>
      );
    } },
    // #156 구분이 재무추가인 입금 → 기사 재무추가 지급(출납 공제의 거울). 넣었으면 정산월·금액.
    { key: 'fin', label: '재무추가', noSort: true, phone: 'hide',
      copy: (r) => (r.fin_mon != null ? `${r.fin_mon === '' ? '이월잔액' : String(r.fin_mon)} ${Math.round(Number(r.fin_amount) || 0)}`
        : Number(r.ifin_ok) > 0 ? `${String(r.ifin_mons)} ${Math.round(Number(r.ifin_amount) || 0)}` : ''), render: (r) => {
      if (!(Number(r.amount_in) > 0)) return '';
      if (r.fin_mon != null) {
        return (
          <button className={`text-left text-xs underline-offset-2 hover:underline ${r.fin_broken ? 'text-amber-700' : 'text-emerald-700'}`}
                  title={r.fin_broken ? '재무추가에서 지워졌거나 금액이 바뀌었습니다' : `재무추가 NO ${String(r.fin_rd)}`} onClick={() => setFinRow(Number(r.bank_tx_id))}>
            넣음 {r.fin_mon === '' ? '이월잔액' : String(r.fin_mon)} · {money(r.fin_amount)}{r.fin_broken ? ' ⚠' : ''}
          </button>
        );
      }
      // #417 기사 계산서 입금 → 재무추가 지급([개인]세금계산서) 자동 — 넣은 줄 · 못 넣은 몫(사유는 마우스를 올리면)
      if (Number(r.ifin_ok) > 0 || Number(r.ifin_pending) > 0) {
        return (
          <span className="text-xs">
            {Number(r.ifin_ok) > 0 && <span className="text-emerald-700" title="세금계산서 입금으로 넣은 재무추가 지급([개인]세금계산서)">
              지급 {String(r.ifin_mons)} · {money(r.ifin_amount)}{Number(r.ifin_ok) > 1 ? ` (${Number(r.ifin_ok)}줄)` : ''}</span>}
            {Number(r.ifin_pending) > 0 && <span className="ml-1 text-amber-700" title={String(r.ifin_notes ?? '')}>⚠ 못 넣음 {Number(r.ifin_pending)}</span>}
          </span>
        );
      }
      if (r.kind !== 'finance') return '';
      return <button className="text-left text-xs text-brand-600 underline-offset-2 hover:underline" onClick={() => setFinRow(Number(r.bank_tx_id))}>넣기…</button>;
    } },
    { key: 'corp_name', label: '법인', phone: 'hide', copy: txt('corp_name') },
    { key: 'bank', label: '은행', copy: txt('bank') },
    { key: 'account_no', label: '계좌번호', phone: 'hide', copy: txt('account_no') },
    { key: 'summary', label: '적요', render: (r) => dash(r.summary), copy: txt('summary') },
    // #172 후속 — 「내용」·「입금」은 눌러 고르고 Ctrl+C 로 복사 · 아래로 끌어 고른 칸을 줄마다 이어 복사(DataTable copy · #250).
    { key: 'memo', label: '내용', render: (r) => dash(r.memo), copy: (r) => String(r.memo ?? '') },
    { key: 'amount_in', label: '입금', align: 'right', render: (r) => (Number(r.amount_in) ? cur(String(r.currency), r.amount_in) : ''),
      copy: amt('amount_in') },
    { key: 'amount_out', label: '지급', align: 'right', render: (r) => (Number(r.amount_out) ? cur(String(r.currency), r.amount_out) : ''), copy: amt('amount_out') },
    { key: 'balance', label: '잔액', align: 'right', render: (r) => cur(String(r.currency), r.balance), copy: amt('balance') },
    { key: 'branch', label: '거래점', phone: 'hide', render: (r) => dash(r.branch), copy: txt('branch') },
    // #12 6번 — 입금시트의 「세금계산서No」 칸. 누르면 연결 창.
    { key: 'invoices', label: '계산서', noSort: true, copy: txt('invoices'), render: (r) => {
      if (!(Number(r.amount_in) > 0) || r.currency !== 'KRW') return '';
      // 1원 미만 차이(계산서 부가세 10전)는 다 연결된 것 — 서버 depositRest 와 같은 기준(#107).
      const partial = Number(r.matched) > 0 && Number(r.amount_in) - Number(r.matched) >= 1;
      return (
        <button className={`text-left text-xs underline-offset-2 hover:underline ${r.invoices ? (partial ? 'text-amber-700' : 'font-mono text-emerald-700') : 'text-brand-600'}`}
                title={partial ? `연결 ${money(r.matched)} / 입금 ${money(r.amount_in)}` : '계산서 연결'} onClick={() => setMatching(Number(r.bank_tx_id))}>
          {r.invoices ? `${String(r.invoices)}${partial ? ' (일부)' : ''}` : '연결…'}
        </button>
      );
    } },
    // #174 연결한 계산서의 거래처 사업자번호.
    { key: 'biz_numbers', label: '사업자번호', noSort: true, phone: 'hide', copy: txt('biz_numbers'), render: (r) => (r.biz_numbers ? <span className="font-mono text-xs">{String(r.biz_numbers)}</span> : '') },
    // 입금시트 「입금전표 적요」 칸. 비어 있으면 연결된 계산서로 자동.
    // #172 적힌 적요는 누르고 Ctrl+C 로 복사(#250) · 두 번 누르면 고치기(빈 칸은 한 번에 적기 창).
    { key: 'slip', label: '입금전표 적요', noSort: true, phone: 'hide', copy: (r) => (Number(r.amount_in) > 0 ? String(r.slip ?? '') : ''), render: (r) => {
      if (!(Number(r.amount_in) > 0)) return '';
      const slip = r.slip ? String(r.slip) : '';
      return (
        <button className={`text-left text-xs underline-offset-2 hover:underline ${slip ? (r.slip_set ? '' : 'text-ink-soft') : 'text-brand-600'}`}
                title={slip ? `${r.slip_set ? '손으로 적은 적요' : '연결된 계산서로 만든 자동 적요'} — 누르고 Ctrl+C 로 복사 · 두 번 누르면 고치기` : '적요 적기'}
                onClick={(e) => (slip ? copySlip(slip, e.currentTarget) : setNoteRow(r))}
                onDoubleClick={() => { if (slip) { window.getSelection()?.removeAllRanges(); setNoteRow(r); } }}>
          {slip || '적기…'}
        </button>
      );
    } },
  ];

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" className="field w-40" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
        <span className="text-ink-faint">~</span>
        <input type="date" className="field w-40" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
        <CorpSelect corps={corps} value={corpId} onChange={(v) => { setCorpId(v); setAccountId(null); setPage(0); }} />
        <AccountSelect corps={corps} corpId={corpId} value={accountId} onChange={(v) => { setAccountId(v); setPage(0); }} />
        <select className="field w-28" value={direction} onChange={(e) => { setDirection(e.target.value as 'in' | 'out' | 'all'); setPage(0); }}>
          <option value="in">입금만</option>
          <option value="out">지급만</option>
          <option value="all">전체</option>
        </select>
        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }} className="flex items-center gap-2">
          <input className="field w-56" placeholder="내용·적요·입금전표 적요·금액" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
        <select className="field w-36" value={match} onChange={(e) => { setMatch(e.target.value as typeof match); setPage(0); }} title="세금계산서 연결(#12)">
          <option value="all">계산서 전체</option>
          <option value="unmatched">계산서 미연결</option>
          <option value="matched">계산서 연결됨</option>
        </select>
        <select className="field w-32" value={kind} onChange={(e) => { setKind(e.target.value as typeof kind); setPage(0); }} title="입금 구분(입금체크 색)">
          <option value="all">구분 전체</option>
          <option value="none">미분류</option>
          {KIND_KEYS.map((x) => <option key={x} value={x}>{DEP_KIND[x].label}</option>)}
        </select>
        <button className="btn ml-auto" onClick={() => setPlanOpen(true)} title="유동성 시재 추정용 — 날짜·금액을 손으로 넣습니다(#351)">수기 입금(추정)</button>
        <button className="btn" onClick={() => setAutoOpen(true)} title="금액이 원 단위까지 같고 거래처 이름이 비슷한 계산서가 한 장뿐인 입금만 잇습니다">자동 연결</button>
      </div>
      {error && <ErrorBox message={error} />}
      {annotError && <ErrorBox message={annotError} />}
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-soft">
        <span>구분 색:</span>
        {KIND_KEYS.map((x) => <span key={x} title={DEP_KIND[x].hint} className={`rounded border border-line px-1.5 py-0.5 ${DEP_KIND[x].bg}`}>{DEP_KIND[x].label}</span>)}
        <span title="입금전표 적요에 「확인필요」라고 적은 줄 — 구분 색보다 먼저 칠합니다" className={`rounded border border-line px-1.5 py-0.5 ${CHECK_NEEDED_BG}`}>확인필요(적요)</span>
        <span className="ml-1">· 계산서를 연결하면 회사오더/기사개인오더가 자동으로 잡힙니다. 손으로 고르면 그 값이 이깁니다.</span>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        {totals.length === 0 && !loading && <span className="text-ink-faint">이 조건의 거래가 없습니다.</span>}
        {totals.map((t) => (
          <span key={t.currency} className="rounded-lg bg-canvas px-3 py-1.5">
            {t.currency} {t.n.toLocaleString()}건 · 입금 <b>{cur(t.currency, t.amount_in)}</b> · 지급 <b>{cur(t.currency, t.amount_out)}</b>
          </span>
        ))}
      </div>
      {/* #472 수기 입금(추정)도 조회 기간에 보이게 — 은행 거래 표와는 따로(입금만·전체일 때) */}
      {direction !== 'out' && <BankInPlanStrip corpId={corpId} accountId={accountId} from={from} to={to} nonce={nonce} onOpen={() => setPlanOpen(true)} />}
      <DataTable tableId="accounting.tx" dragSum columns={columns} rows={rows} loading={loading} sort={sort} onSort={(k) => { toggleSort(k); setPage(0); }}
                 onCopy={({ label, count, text, error: err }) => say(err ? `복사하지 못했습니다: ${err}`
                   : count > 1 ? `${label} ${count}칸 복사` : `${label} 복사: ${text.length > 40 ? `${text.slice(0, 40)}…` : text}`)}
                 rowClass={(r) => (r.slip_set && String(r.slip ?? '').includes('확인필요') ? CHECK_NEEDED_BG
                   : r.kind ? DEP_KIND[r.kind as DepKind]?.bg ?? '' : '')} />
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={200} />
      {matching !== null && (
        <Modal width="max-w-4xl">
          <DepositMatchForm bankTxId={matching} onClose={() => setMatching(null)} onChanged={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {finRow !== null && (
        <Modal width="max-w-3xl">
          <BankFinForm bankTxId={finRow} onClose={() => setFinRow(null)} onChanged={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {noteRow && (
        <Modal width="max-w-xl">
          <SlipNoteForm row={noteRow} below={(() => { const ins = rows.filter((r) => Number(r.amount_in) > 0); const i = ins.findIndex((r) => r.bank_tx_id === noteRow.bank_tx_id); return i < 0 ? [noteRow] : ins.slice(i); })()} onClose={() => setNoteRow(null)} onDone={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {planOpen && (
        <Modal width="max-w-4xl">
          <BankInPlanForm corps={corps} from={from} to={to} defaultCorp={corpId} defaultAccount={accountId}
                          onClose={() => { setPlanOpen(false); setNonce((n) => n + 1); }} />
        </Modal>
      )}
      {autoOpen && (
        <Modal width="max-w-4xl">
          <AutoMatchForm dateFrom={from} dateTo={to} corpId={corpId} onClose={() => setAutoOpen(false)} onDone={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {toast && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 max-w-[90vw] -translate-x-1/2 truncate rounded-lg bg-ink px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 일자별 잔액 (#13)
// ─────────────────────────────────────────────────────────────────────────────
function BalanceTab({ corps }: { corps: AcctCorp[] }) {
  const fitRef = useRef<HTMLDivElement>(null);
  const fitH = useFitHeight(fitRef);
  // 기본 = 전날(#68) — 은행 잔액 파일은 전날 마감분을 아침에 올린다.
  const [date, setDate] = useSticky('acct.balance.date', yesterday());
  const [corpId, setCorpId] = useAcctCorp();
  const [data, setData] = useState<BankBalanceResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.getBankBalances({ date, corpId })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, corpId]);

  // 법인 순서대로 묶는다(서버가 이미 법인 순으로 정렬해 준다).
  const groups = useMemo(() => {
    const out: { corpName: string; rows: BankBalanceResult['rows'] }[] = [];
    for (const r of data?.rows ?? []) {
      const last = out[out.length - 1];
      if (last && last.corpName === r.corpName) last.rows.push(r);
      else out.push({ corpName: r.corpName, rows: [r] });
    }
    return out;
  }, [data]);
  const totalOf = (corpName: string | null) => data?.totals.filter((t) => t.corpName === corpName) ?? [];
  const noData = data && data.rows.every((r) => r.balance === null);

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" className="field w-40" value={date} onChange={(e) => setDate(e.target.value)} />
        <CorpSelect corps={corps} value={corpId} onChange={setCorpId} />
        <span className="text-xs text-ink-faint">
          고른 날짜의 잔액입니다. 그 날 자료가 없는 계좌는 <b>가장 최근 자료</b>를 이월해 보여 줍니다(기준일 표시).
        </span>
      </div>
      <BalanceChecks date={date} />
      {error && <ErrorBox message={error} />}
      {noData && <p className="text-sm text-ink-soft">아직 올린 잔액·거래 자료가 없습니다. 「은행 파일 올리기」에서 잔액조회 파일을 올려 주세요.</p>}
      <TopScrollbar target={fitRef} />
      <div ref={fitRef} className="overflow-auto" style={{ maxHeight: fitH }}>
        <table className="w-full min-w-[900px] text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-surface">
          <thead>
            <tr className="border-b text-left text-ink-soft">
              <th className="px-2 py-2">법인</th><th className="px-2">은행</th><th className="px-2">계좌번호</th><th className="px-2">별칭</th>
              <th className="px-2 text-right">입금액</th><th className="px-2 text-right">출금액</th><th className="px-2 text-right">잔액</th>
              <th className="px-2">기준일</th>
            </tr>
          </thead>
          <tbody className={loading ? 'opacity-50' : ''}>
            {groups.map((g) => (
              <GroupRows key={g.corpName} corpName={g.corpName} rows={g.rows} totals={totalOf(g.corpName)} date={date} />
            ))}
            {totalOf(null).map((t) => (
              <tr key={`all-${t.currency}`} className="border-t-2 bg-brand-50 font-bold">
                <td className="px-2 py-2" colSpan={4}>총 합계 {t.currency !== 'KRW' ? `(${t.currency})` : ''} · {t.n}좌</td>
                <td className="px-2 text-right">{cur(t.currency, t.amountIn)}</td>
                <td className="px-2 text-right">{cur(t.currency, t.amountOut)}</td>
                <td className="px-2 text-right">{cur(t.currency, t.balance)}</td>
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * 법인별 확인 버튼(#94) — 시제조회·위하고 보통예금·일자별 잔액 셋이 맞으면 그 법인 담당자가 누른다.
 * 날짜마다 서버에 따로 남아서 다른 날짜를 봤다가 돌아와도 그대로다. 다시 누르면 풀린다.
 */
function BalanceChecks({ date }: { date: string }) {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [rows, setRows] = useState<BalanceCheck[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setErr(null);
    api.listBalanceChecks({ date })
      .then((r) => { if (!cancelled) setRows(r.rows); })
      .catch((e) => { if (!cancelled) { setRows(null); setErr(e instanceof Error ? e.message : String(e)); } });
    return () => { cancelled = true; };
  }, [date]);
  async function toggle(c: BalanceCheck) {
    if (!canWrite || busy !== null) return;
    setBusy(c.corpId); setErr(null);
    try {
      await api.setBalanceCheck({ date, corpId: c.corpId, checked: !c.checked });
      const r = await api.listBalanceChecks({ date });
      setRows(r.rows);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  }
  // 목록을 못 받으면(배포 순서상 함수가 아직 없을 때 등) 칸을 아예 안 그린다 — 잔액 표는 그대로.
  if (!rows) return null;
  const done = rows.filter((c) => c.checked).length;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium">{date} 잔액 확인</span>
      {rows.map((c) => (
        <button key={c.corpId} type="button" disabled={!canWrite || busy !== null}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium disabled:cursor-default ${c.checked
                  ? 'border-emerald-600 bg-emerald-600 text-white'
                  : 'border-ink-faint/50 bg-surface text-ink-soft hover:border-emerald-600 hover:text-emerald-700'}`}
                title={c.by ? `${c.checked ? '확인' : '확인 풀림'} · ${c.by} · ${c.at ?? ''}${canWrite ? ' — 다시 누르면 ' + (c.checked ? '풀립니다' : '확인됩니다') : ''}`
                  : canWrite ? '시제조회·위하고 보통예금·일자별 잔액이 맞으면 누르세요' : ''}
                onClick={() => void toggle(c)}>
          {c.checked ? '✓ ' : ''}{c.corpName}
          {c.checked && c.by && <span className="ml-1 text-[11px] font-normal opacity-90">{c.by}</span>}
        </button>
      ))}
      <span className="text-xs text-ink-faint">{done}/{rows.length} 확인</span>
      {err && <span className="text-xs text-red-600">{err}</span>}
    </div>
  );
}

function GroupRows({ corpName, rows, totals, date }: {
  corpName: string; rows: BankBalanceResult['rows']; totals: BankBalanceResult['totals']; date: string;
}) {
  return (
    <>
      {rows.map((r) => (
        <tr key={r.bankAccountId} className={`border-b ${r.isActive ? '' : 'text-ink-faint'}`}>
          <td className="px-2 py-1.5">{r.corpName}</td>
          <td className="px-2">{r.bank}</td>
          <td className="px-2 font-mono text-xs">{r.accountNo}</td>
          <td className="px-2 text-ink-soft">{dash(r.alias)}</td>
          <td className="px-2 text-right">{r.carried ? '' : cur(r.currency, r.amountIn)}</td>
          <td className="px-2 text-right">{r.carried ? '' : cur(r.currency, r.amountOut)}</td>
          <td className="px-2 text-right font-medium">
            {r.balance === null ? <span className="text-ink-faint">자료 없음</span> : cur(r.currency, r.balance)}
            {r.mismatch && (
              <div className="text-xs text-amber-700" title="잔액조회 파일과 거래내역의 같은 날 마지막 잔액이 다릅니다">
                거래내역 {cur(r.currency, r.txBalance)}
              </div>
            )}
          </td>
          <td className="px-2 text-xs">
            {r.asOf === null ? '' : r.asOf === date ? <span className="text-ink-soft">당일</span>
              : <span className="text-amber-700">{r.asOf} 이월</span>}
          </td>
        </tr>
      ))}
      {totals.map((t) => (
        <tr key={`${corpName}-${t.currency}`} className="border-b bg-canvas font-semibold">
          <td className="px-2 py-1.5" colSpan={4}>{corpName} 소계 {t.currency !== 'KRW' ? `(${t.currency})` : ''}</td>
          <td className="px-2 text-right">{cur(t.currency, t.amountIn)}</td>
          <td className="px-2 text-right">{cur(t.currency, t.amountOut)}</td>
          <td className="px-2 text-right">{cur(t.currency, t.balance)}</td>
          <td />
        </tr>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 은행 파일 올리기
// ─────────────────────────────────────────────────────────────────────────────
const KIND_LABEL: Record<string, string> = { account: '계좌리스트', tx: '일자별 거래내역', balance: '일자별 잔액조회' };

function UploadTab() {
  const [sheet, setSheet] = useState<BankCell[][] | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<BankUploadResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [nonce, setNonce] = useState(0);
  const [confirmRevert, setConfirmRevert] = useState<number | null>(null);

  const { rows: uploads, loading } = useList(
    () => api.listBankUploads({ limit: 50 }) as unknown as Promise<ListResultLike>, [nonce]);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('parse'); setError(null); setPreview(null); setNotice(null);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]!];
      if (!ws) throw new Error('시트를 찾을 수 없습니다.');
      // 🔴 raw 그대로 넘긴다 — 금액은 숫자, 계좌번호는 하이픈 든 문자열이다. 판별·정규화는 서버가 한다.
      const aoa = XLSX.utils.sheet_to_json<BankCell[]>(ws, { header: 1, defval: '', raw: true, blankrows: false });
      setSheet(aoa); setFileName(file.name);
      setBusy('check');
      setPreview(await api.previewBankUpload({ fileName: file.name, sheet: aoa }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSheet(null); setFileName('');
    } finally {
      setBusy(null);
      e.target.value = '';
    }
  }

  async function commit() {
    if (!sheet) return;
    setBusy('commit'); setError(null);
    try {
      const r = await api.commitBankUpload({ fileName, sheet });
      setNotice(`${KIND_LABEL[r.kind]} ${r.rowsNew.toLocaleString()}건을 반영했습니다${r.rowsDup ? ` (이미 있던 ${r.rowsDup.toLocaleString()}건은 건너뜀)` : ''}.`);
      setSheet(null); setPreview(null); setFileName('');
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  async function revert(id: number) {
    setBusy(`revert-${id}`); setError(null); setConfirmRevert(null);
    try {
      const r = await api.revertBankUpload({ id });
      setNotice(`업로드 #${id} 를 취소했습니다(${r.rows.toLocaleString()}건 내림).`);
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  const s = preview?.summary;
  return (
    <div className="space-y-4">
      <div className="card space-y-3 p-4">
        <p className="text-sm text-ink-soft">
          인사이드뱅크에서 내려받은 엑셀을 <b>그대로</b> 올리세요. 어떤 파일인지는 머리글을 보고 알아서 가립니다 —
          <b> 계좌리스트</b>(계좌통합조회) · <b>일자별 거래내역</b> · <b>일자별 잔액조회</b>.
          처음 한 번은 <b>계좌리스트</b>를 먼저 올려야 합니다(계좌번호로 법인을 정합니다).
        </p>
        <label className="btn-primary inline-block cursor-pointer">
          {busy === 'parse' || busy === 'check' ? '확인 중…' : '파일 고르기'}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={pick} disabled={busy !== null} />
        </label>
        {error && <ErrorBox message={error} />}

        {preview && s && (
          <div className="space-y-2 rounded-lg border p-3 text-sm">
            <div className="font-medium">
              {fileName} → <b>{KIND_LABEL[preview.kind]}</b>
              {s.dateFrom && <span className="ml-2 text-ink-soft">{s.dateFrom === s.dateTo ? s.dateFrom : `${s.dateFrom} ~ ${s.dateTo}`}</span>}
            </div>
            <div className="flex flex-wrap gap-3">
              <span>전체 {s.total.toLocaleString()}</span>
              <span className="text-emerald-700">새로 {s.new.toLocaleString()}</span>
              {s.dup !== undefined && <span className="text-ink-soft">이미 있음 {s.dup.toLocaleString()}</span>}
              {s.same !== undefined && <span className="text-ink-soft">같음 {s.same.toLocaleString()}</span>}
              {s.changed !== undefined && s.changed > 0 && <span className="text-amber-700">바뀜 {s.changed}</span>}
              {s.overwrite !== undefined && s.overwrite > 0 && <span className="text-amber-700">덮어씀 {s.overwrite}</span>}
            </div>
            {preview.errorCount > 0 && (
              <div className="rounded bg-red-50 p-2 text-red-700">
                <b>올릴 수 없습니다 — {preview.errorCount}건</b>
                <ul className="ml-4 list-disc">{preview.errors.map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            )}
            {preview.warnings.length > 0 && (
              <div className="rounded bg-amber-50 p-2 text-amber-800">
                <b>확인하세요</b>
                <ul className="ml-4 list-disc">{preview.warnings.map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            )}
            {preview.sample && preview.sample.length > 0 && (
              <ul className="max-h-48 overflow-auto text-xs">
                {preview.sample.map((a) => (
                  <li key={a.accountNo}>{a.status === 'new' ? '새 계좌' : '바뀜'} · {a.corpName} · {a.bank} {a.accountNo} {a.alias && `(${a.alias})`}</li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <button className="btn-primary" onClick={commit} disabled={busy !== null || preview.errorCount > 0 || s.new + (s.changed ?? 0) + (s.overwrite ?? 0) === 0}>
                {busy === 'commit' ? '반영 중…' : '반영'}
              </button>
              <button className="btn-ghost" onClick={() => { setPreview(null); setSheet(null); }}>취소</button>
            </div>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold">올린 기록</h2>
        <DataTable tableId="accounting.uploads" loading={loading} rows={uploads} columns={[
          { key: 'bank_upload_id', label: '#' },
          { key: 'created_at', label: '올린 시각', render: (r) => String(r.created_at ?? '').slice(0, 16).replace('T', ' ') },
          { key: 'created_by', label: '올린 사람', render: (r) => String(r.created_by ?? '').split('@')[0] },
          { key: 'kind', label: '종류', render: (r) => KIND_LABEL[String(r.kind)] ?? String(r.kind) },
          { key: 'file_name', label: '파일', render: (r) => dash(r.file_name) },
          { key: 'date_from', label: '기간', render: (r) => (r.date_from ? (r.date_from === r.date_to ? String(r.date_from) : `${r.date_from} ~ ${r.date_to}`) : '') },
          { key: 'rows_new', label: '반영', align: 'right' },
          { key: 'rows_dup', label: '건너뜀', align: 'right' },
          { key: 'warnings', label: '경고', render: (r) => (r.warnings ? <span className="text-amber-700" title={(JSON.parse(String(r.warnings)) as string[]).join('\n')}>{(JSON.parse(String(r.warnings)) as string[]).length}건</span> : '') },
          { key: 'act', label: '', render: (r) => {
            const u = r as unknown as BankUploadRow;
            if (u.is_deleted) return <span className="text-ink-faint">취소됨</span>;
            if (u.kind === 'account') return '';
            return confirmRevert === u.bank_upload_id ? (
              <span className="flex gap-1">
                <button className="btn-ghost py-1 text-xs text-red-600" onClick={() => revert(u.bank_upload_id)} disabled={busy !== null}>정말 취소</button>
                <button className="btn-ghost py-1 text-xs" onClick={() => setConfirmRevert(null)}>아니오</button>
              </span>
            ) : (
              <button className="btn-ghost py-1 text-xs" onClick={() => setConfirmRevert(u.bank_upload_id)} disabled={busy !== null}>업로드 취소</button>
            );
          } },
        ]} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 계좌 관리
// ─────────────────────────────────────────────────────────────────────────────
function AccountsTab({ corps }: { corps: AcctCorp[] }) {
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<BankAccountRow | 'new' | null>(null);
  const { rows, loading, error } = useList(() => api.listBankAccounts() as unknown as Promise<ListResultLike>, [nonce]);

  const columns: Column[] = [
    { key: 'corp_name', label: '법인' },
    { key: 'bank', label: '은행' },
    { key: 'account_no', label: '계좌번호' },
    { key: 'product', label: '상품명', render: (r) => dash(r.product) },
    { key: 'alias', label: '별칭(은행)', render: (r) => dash(r.alias) },
    { key: 'currency', label: '통화' },
    { key: 'is_active', label: '사용', render: (r) => (r.is_active ? '사용' : <span className="text-ink-faint">안 함</span>) },
    { key: 'last_tx_date', label: '마지막 거래', render: (r) => dash(r.last_tx_date) },
    { key: 'last_bal_date', label: '마지막 잔액', render: (r) => dash(r.last_bal_date) },
  ];

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <p className="page-hint min-w-0 flex-1">
          계좌리스트 파일을 올리면 채워집니다. 목록에 안 나오는 계좌(외화 등)는 여기서 추가하세요.
          <b> 별칭은 은행에 등록된 이름이라 옛 회사명일 수 있습니다</b> — 법인은 「법인」 칸이 정본입니다.
        </p>
        <button className="btn ml-auto" onClick={() => setEditing('new')}>계좌 추가</button>
      </div>
      {error && <ErrorBox message={error} />}
      <DataTable tableId="accounting.accounts" columns={columns} rows={rows} loading={loading}
                 onRowClick={(r) => setEditing(r as unknown as BankAccountRow)} />
      {editing && (
        <Modal width="max-w-xl">
          <AccountForm corps={corps} row={editing === 'new' ? null : editing}
                       onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setNonce((n) => n + 1); }} />
        </Modal>
      )}
    </div>
  );
}

function AccountForm({ corps, row, onClose, onSaved }: {
  corps: AcctCorp[]; row: BankAccountRow | null; onClose: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState({
    corpId: row?.corp_id ?? corps[0]?.corp_id ?? 0, bank: row?.bank ?? '', accountNo: row?.account_no ?? '',
    product: row?.product ?? '', alias: row?.alias ?? '', currency: row?.currency ?? 'KRW', isActive: row?.is_active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: k === 'corpId' ? Number(e.target.value) : e.target.value }));

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.saveBankAccount({ id: row?.bank_account_id, ...f });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="card space-y-4 p-5">
      <h2 className="font-semibold">{row ? '계좌 수정' : '계좌 추가'}</h2>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="space-y-1">법인
          <select className="field w-full" value={f.corpId} onChange={set('corpId')}>
            {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name} · {c.name}</option>)}
          </select>
        </label>
        <label className="space-y-1">은행<input className="field w-full" value={f.bank} onChange={set('bank')} placeholder="신한" /></label>
        <label className="space-y-1">계좌번호<input className="field w-full font-mono" value={f.accountNo} onChange={set('accountNo')} placeholder="은행 파일과 똑같이(하이픈 포함)" /></label>
        <label className="space-y-1">통화<input className="field w-full" value={f.currency} onChange={set('currency')} /></label>
        <label className="space-y-1">상품명<input className="field w-full" value={f.product} onChange={set('product')} /></label>
        <label className="space-y-1">별칭<input className="field w-full" value={f.alias} onChange={set('alias')} /></label>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <input id="acc-active" type="checkbox" checked={f.isActive} onChange={(e) => setF((x) => ({ ...x, isActive: e.target.checked }))} />
        <span>사용(끄면 잔액 화면에서 빠집니다)</span>
      </div>
      {error && <ErrorBox message={error} />}
      <div className="flex gap-2">
        <button className="btn-primary" onClick={save} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}
